import type {
  UploadAiProduct,
  UploadAiProductStatus,
  UploadRow,
} from '@/models/upload';
import { aiProductName } from './aiProductName';

/**
 * When ingestion sends DICOMs to AI as each is stored (AP-952, off by
 * default), the row also says how many have been sent while the rest still
 * process. No total there: the number to send grows as files are processed.
 */
const dicomLabel = ({
  filesProcessed,
  filesTotal,
  aiDicomsSent,
}: UploadRow): string => {
  const processing = filesTotal
    ? `Processing DICOMs · ${filesProcessed ?? 0}/${filesTotal}`
    : 'Processing DICOMs';
  return aiDicomsSent
    ? `${processing} · Sent to AI · ${aiDicomsSent}`
    : processing;
};

const isTerminal = ({ status }: UploadAiProduct) =>
  status === 'COMPLETED' || status === 'FAILED';

/**
 * `n/m` counts finished products (completed or failed) of all of them, so it
 * reads the same whether the scans run one after another or all at once
 * (AP-869). Each product's own state is listed by `aiProductStates`.
 */
const aiLabel = ({
  aiProducts,
  aiDicomsSent,
  aiDicomsTotal,
}: UploadRow): string => {
  if (aiProducts.length === 0) {
    // DICOMs are going to Encephalon; the scans start once they are all there.
    if (aiDicomsTotal) {
      return `Sending to AI · ${aiDicomsSent ?? 0}/${aiDicomsTotal}`;
    }
    // No scan yet, or AI is disabled at this site: the study is viewable.
    return 'Ready to view · AI pending';
  }
  const done = aiProducts.filter(isTerminal).length;
  return `AI processing · ${done}/${aiProducts.length}`;
};

export type AiProductState =
  | 'queued'
  | 'uploading'
  | 'scanning'
  | 'done'
  | 'failed';

const AI_PRODUCT_STATE: Record<UploadAiProductStatus, AiProductState> = {
  PENDING: 'queued',
  UPLOADING: 'uploading',
  SCANNING: 'scanning',
  COMPLETED: 'done',
  FAILED: 'failed',
};

/**
 * Every AI product of an upload at the AI stage, with its state, in the
 * order the API lists them (AP-869). Empty at any other stage.
 */
export const aiProductStates = (
  row: UploadRow
): { product: string; name: string; state: AiProductState }[] =>
  row.stage === 'AI'
    ? row.aiProducts.map(({ product, status }) => ({
        product,
        name: aiProductName(product),
        state: AI_PRODUCT_STATE[status] ?? 'queued',
      }))
    : [];

/** The Status cell text for an upload, by its stage (AP-899 R4). */
export function uploadStatusLabel(row: UploadRow): string {
  switch (row.stage) {
    case 'DICOM':
      return dicomLabel(row);
    case 'AI':
      return aiLabel(row);
    case 'DONE':
      return 'Done';
    case 'FAILED':
      return 'Failed';
    default:
      return '';
  }
}

export type UploadBadgeVariant =
  | 'processing'
  | 'failed'
  | 'success'
  | 'warning';

/** Failed AI products of an upload, by display name. */
export const failedProductNames = ({ aiProducts }: UploadRow): string[] =>
  aiProducts
    .filter((p) => p.status === 'FAILED')
    .map((p) => aiProductName(p.product));

/** The badge's `data-variant` (AP-899 R6). */
export function uploadBadgeVariant(row: UploadRow): UploadBadgeVariant {
  switch (row.stage) {
    case 'FAILED':
      return 'failed';
    case 'DONE':
      return row.error || failedProductNames(row).length > 0
        ? 'warning'
        : 'success';
    default:
      return 'processing';
  }
}

/**
 * The badge tooltip (AP-899 R7): the error and the failed products, for a
 * warning DONE row or a FAILED row with an error. Null when there is none.
 */
export function uploadStatusTooltip(row: UploadRow): string | null {
  const shows =
    uploadBadgeVariant(row) === 'warning' ||
    (row.stage === 'FAILED' && !!row.error);
  if (!shows) return null;
  const products = failedProductNames(row).join(', ');
  return [row.error, products].filter(Boolean).join(' · ');
}
